import { useState, useEffect, useCallback } from 'react';
import socketService from '../../../../../packages/shared/socket';
import API_ENDPOINTS from '../../config/api';
import { useFormCheckState } from '../../hooks/useFormCheckUpdates';
import { FORM_CHECK_STAGES } from '../../utils/formCheckDisplay';

/**
 * Impact-path map data: pathways, multi-parent connections, form-check state,
 * and the live socket updates that keep them fresh.
 *
 * Extracted verbatim from ImpactPathView so a second screen can mount the same
 * map without inheriting 2000 lines of view chrome (specs/impact-path-v2-prd.md
 * §1). Behaviour is deliberately unchanged — same query params, same relevance
 * rules, same node_analysis merge, same logging.
 *
 * Form-check state lives here rather than in the screen because it needs
 * `setPathways` (it writes results onto records) and the socket handler needs
 * `markPending` — keeping the pair together avoids passing setters back and
 * forth across the boundary.
 *
 * The screen keeps the setters: optimistic updates, clearing on scenario
 * switch, and node removal all still happen there.
 *
 * @param {object} params
 * @param {string} params.kid                 Instance id from the URL
 * @param {string} params.instanceId          Instance id from context/props
 * @param {string} params.scenarioId
 * @param {string} params.participantId
 * @param {'individual'|'scenario'|'instance'} params.viewMode
 * @param {string|null} params.tulevaisuuskuva
 * @param {(data: object, node: object|undefined) => void} [params.onNodeAnalyzed]
 *        Called when a node's background AI analysis lands, with the node as it
 *        was before the merge. The screen uses it for unread badges and for
 *        clearing the processing glimmer.
 * @param {boolean} [params.includeProposed]  Also load `status = 'proposed'`
 *        nodes — cards drafted from a team's documents that a person has not
 *        yet kept (specs/workshop-modules/impact_path.md). The v2 canvas asks
 *        for them; v1 never sees them.
 */
export function useImpactPathData({
  kid,
  instanceId,
  scenarioId,
  participantId,
  viewMode,
  tulevaisuuskuva,
  onNodeAnalyzed,
  includeProposed = false,
}) {
  const [pathways, setPathways] = useState([]);
  const [connections, setConnections] = useState([]); // Multi-parent connections from pathway_connections table
  const [blockers, setBlockers] = useState([]); // Map objects pinned to edges (v2 §4)
  const [loops, setLoops] = useState([]); // Recognised feedback loops (v2 §5)
  const [blockerLinks, setBlockerLinks] = useState([]); // Links with a blocker at one end
  const [pendingSuggestions, setPendingSuggestions] = useState([]); // AI proposals awaiting a decision
  const [loading, setLoading] = useState(true);
  const [participantInfo, setParticipantInfo] = useState(null);
  const [scenarioInfo, setScenarioInfo] = useState(null);

  const formCheck = useFormCheckState({
    instanceId: kid || instanceId,
    stage: FORM_CHECK_STAGES.IMPACT_PATH_NODE,
    setRecords: setPathways,
  });
  const { markPending } = formCheck;

  // Reusable function to load/refresh pathways
  const loadPathways = useCallback(async () => {
    try {
      // Build query params based on current view mode
      const params = new URLSearchParams();
      if (kid || instanceId) params.append('kid', kid || instanceId);

      // Apply filters based on view mode
      if (viewMode === 'individual' && participantId) {
        params.append('participantId', participantId);
      }
      if ((viewMode === 'individual' || viewMode === 'scenario') && scenarioId) {
        params.append('scenarioId', scenarioId);
      }
      // Filter by tulevaisuuskuva (future) when selected.
      // Only in Individual / Scenario modes — Kaikki (instance) must NOT lock
      // to one future, otherwise `scenarioId` lingering in localStorage drags
      // its `selected_tulevaisuuskuva` into the request and the backend
      // returns only that one scenario's nodes. Same gate as `scenarioId`
      // above.
      if (tulevaisuuskuva && (viewMode === 'individual' || viewMode === 'scenario')) {
        params.append('tulevaisuuskuva', tulevaisuuskuva);
      } else {
        // Kaikki (instance) view, or no explicit future selected — use each
        // scenario's own selected_tulevaisuuskuva. Prevents duplicate scenario
        // trees and lets Kaikki show every scenario.
        params.append('useSelectedFutures', 'true');
      }
      if (includeProposed) params.append('includeProposed', 'true');

      console.log('📡 Loading pathways for:', { kid, instanceId, scenarioId, participantId, tulevaisuuskuva, viewMode });
      const fetchUrl = API_ENDPOINTS.PATHWAYS(params.toString());
      console.log('📡 Fetch URL:', fetchUrl);

      const response = await fetch(fetchUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Loaded pathways:', data);
      console.log('✅ Setting pathways state:', data.pathways?.length || 0, 'pathways');
      console.log('🔗 Multi-parent connections:', data.connections?.length || 0);

      // Map ai_sources to references for frontend consistency
      const pathwaysWithReferences = (data.pathways || []).map(p => ({
        ...p,
        references: p.ai_sources || []
      }));
      setPathways(pathwaysWithReferences);
      setConnections(data.connections || []); // Store multi-parent connections
      setBlockers(data.blockers || []);
      setLoops(data.loops || []);
      setBlockerLinks(data.links || []);
      setPendingSuggestions(data.suggestions || []);
      setParticipantInfo(data.participant);
      setScenarioInfo(data.scenario);
      setLoading(false);
    } catch (error) {
      console.error('❌ Failed to load pathways:', error);
      setLoading(false);
    }
  }, [scenarioId, participantId, kid, instanceId, viewMode, tulevaisuuskuva, includeProposed]);

  // Single controlled async flow: load pathways when we have required data
  // NO RACE CONDITIONS - we wait for all required data before proceeding
  useEffect(() => {
    // Individual + Scenario modes both need a scenarioId — show selector if missing
    if ((viewMode === 'individual' || viewMode === 'scenario') && !scenarioId) {
      console.log('⏳ Waiting for scenario selection...');
      setLoading(false); // Allow scenario selector to show
      return;
    }

    // Must have instance ID to load anything
    if (!kid && !instanceId) {
      console.log('⏳ Waiting for instance ID...');
      return; // Keep loading=true until we have instance
    }

    // Load pathways (tulevaisuuskuva derived from scenario's selected_tulevaisuuskuva)
    console.log('🚀 Loading pathways:', { scenarioId, tulevaisuuskuva, viewMode });
    loadPathways();
  }, [loadPathways, viewMode, scenarioId, tulevaisuuskuva, kid, instanceId]);

  // The screen connects the socket in its own effect, and the instance id it
  // needs can arrive from an async fetch — so socketService.socket is often
  // still null when this hook first runs, and an early `return` would never
  // re-subscribe. Watch until the socket exists, then let the effect below
  // re-run. (Same class of bug as the one CLAUDE.md documents for emits.)
  const [socketReady, setSocketReady] = useState(!!socketService.socket);
  useEffect(() => {
    if (socketService.socket) {
      setSocketReady(true);
      return undefined;
    }
    const timer = setInterval(() => {
      if (socketService.socket) {
        setSocketReady(true);
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, [socketReady]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!socketService.socket) return;

    const handlePathwayUpdate = (data) => {
      console.log('🔄 Pathway update received:', data);

      const dataKid = data.kid || data.instanceId;
      const currentKid = kid || instanceId;

      // Extract node data from the event (backend sends 'nodeData')
      const nodeData = data.nodeData || data.pathway;
      if (!nodeData) {
        console.warn('⚠️ No node data in update event');
        return;
      }

      // Check if this update is relevant to current view
      const isRelevantInstance = dataKid === currentKid;

      let isRelevant = false;
      if (viewMode === 'individual') {
        // Individual mode: must match both scenario AND participant
        isRelevant = isRelevantInstance &&
                     (nodeData.scenario_id === scenarioId) &&
                     (data.participantId === participantId);
      } else if (viewMode === 'scenario') {
        // Scenario mode: must match scenario (any participant)
        isRelevant = isRelevantInstance && (nodeData.scenario_id === scenarioId);
      } else if (viewMode === 'instance') {
        // Instance mode: must match instance (any scenario, any participant)
        isRelevant = isRelevantInstance;
      }

      if (isRelevant) {
        console.log('✅ Relevant update, applying to pathways');
        // Update or add pathway
        setPathways(prev => {
          // A rejected proposal leaves the canvas (kept in the table).
          if (nodeData.status === 'rejected') return prev.filter((p) => p.id !== nodeData.id);
          const existing = prev.find(p => p.id === nodeData.id);
          if (existing) {
            console.log('  📝 Updating existing pathway:', nodeData.id, 'nodeData has node_analysis:', !!nodeData.node_analysis, 'existing has:', !!existing.node_analysis);
            return prev.map(p => p.id === nodeData.id ? {
              ...p,
              ...nodeData,
              node_analysis: (() => {
                if (!nodeData.node_analysis) return p.node_analysis;
                const merged = { ...(p.node_analysis || {}), ...nodeData.node_analysis };
                // Incoming blob without formCheck means the server cleared it
                // (text edit). Spreading would otherwise resurrect the stale warn.
                if (!Object.prototype.hasOwnProperty.call(nodeData.node_analysis, 'formCheck')) {
                  delete merged.formCheck;
                }
                return merged;
              })(),
            } : p);
          } else {
            console.log('  ➕ Adding new pathway:', nodeData.id);
            // Starters (muuttujat) skip the form check — no pending shimmer.
            if (
              participantId
              && nodeData.participantId === participantId
              && nodeData.is_scenario_starter !== true
            ) {
              markPending(nodeData.id);
            }
            return [...prev, nodeData];
          }
        });
      } else {
        console.log('⏭️ Update not relevant to current view, skipping');
      }
    };

    // Handle vote updates in real-time
    const handleVoteUpdate = (data) => {
      console.log('🗳️ Vote update received:', data);
      setPathways(prev => prev.map(p =>
        p.id === data.nodeId
          ? {
              ...p,
              votesUp: data.votesUp,
              votesDown: data.votesDown,
              reactions: data.votesUp - data.votesDown
            }
          : p
      ));
    };

    // Handle async AI analysis results for nodes — merge the data here, let the
    // screen deal with unread badges and the processing glimmer.
    const handleNodeAnalyzed = (data) => {
      console.log('🔬 Node analysis received:', data.nodeId);
      setPathways((prev) => {
        const node = prev.find((p) => p.id === data.nodeId);
        onNodeAnalyzed?.(data, node);
        return prev.map((p) =>
          p.id === data.nodeId
            ? { ...p, node_analysis: { ...(p.node_analysis || {}), ...(data.node_analysis || {}) } }
            : p,
        );
      });
    };

    // Blockers arrive and get analysed asynchronously, same as node analysis.
    const handleBlockerAdded = (data) => {
      if (!data?.blocker) return;
      setBlockers((prev) => (prev.some((b) => b.id === data.blocker.id) ? prev : [...prev, data.blocker]));
    };
    const handleSuggestionsAdded = (data) => {
      if (!data?.suggestions?.length) return;
      setPendingSuggestions((prev) => {
        const known = new Set(prev.map((p) => p.id));
        return [...prev, ...data.suggestions.filter((s) => !known.has(s.id))];
      });
    };
    const handleBlockerRemoved = (data) => {
      setBlockers((prev) => prev.filter((b) => b.id !== data.blockerId));
    };
    // A deleted node takes its whole subtree; the server lists every id.
    const handleNodeRemoved = (data) => {
      if (!data?.nodeIds?.length || data.instanceId !== (kid || instanceId)) return;
      const gone = new Set(data.nodeIds);
      setPathways((prev) => prev.filter((p) => !gone.has(p.id)));
    };
    const handleBlockerAnalyzed = (data) => {
      setBlockers((prev) => prev.map((b) => (b.id === data.blockerId ? { ...b, analysis: data.analysis } : b)));
    };

    socketService.socket.on('impact-path-blocker-added', handleBlockerAdded);
    socketService.socket.on('impact-path-suggestions-added', handleSuggestionsAdded);
    socketService.socket.on('impact-path-blocker-removed', handleBlockerRemoved);
    socketService.socket.on('impact-path-blocker-analyzed', handleBlockerAnalyzed);
    socketService.socket.on('impact-path-node-added', handlePathwayUpdate);
    socketService.socket.on('impact-path-node-updated', handlePathwayUpdate);
    socketService.socket.on('impact-path-node-removed', handleNodeRemoved);
    socketService.socket.on('node-vote-updated', handleVoteUpdate);
    socketService.socket.on('impact-path-node-analyzed', handleNodeAnalyzed);

    console.log('🔌 Socket subscribed to pathway updates. Mode:', viewMode);

    return () => {
      socketService.socket.off('impact-path-blocker-added', handleBlockerAdded);
      socketService.socket.off('impact-path-suggestions-added', handleSuggestionsAdded);
      socketService.socket.off('impact-path-blocker-removed', handleBlockerRemoved);
      socketService.socket.off('impact-path-blocker-analyzed', handleBlockerAnalyzed);
      socketService.socket.off('impact-path-node-added', handlePathwayUpdate);
      socketService.socket.off('impact-path-node-updated', handlePathwayUpdate);
      socketService.socket.off('impact-path-node-removed', handleNodeRemoved);
      socketService.socket.off('node-vote-updated', handleVoteUpdate);
      socketService.socket.off('impact-path-node-analyzed', handleNodeAnalyzed);
      console.log('🔌 Socket unsubscribed from pathway updates');
    };
  }, [scenarioId, participantId, kid, instanceId, viewMode, markPending, onNodeAnalyzed, socketReady]);

  return {
    pathways,
    setPathways,
    connections,
    setConnections,
    blockers,
    setBlockers,
    loops,
    setLoops,
    blockerLinks,
    setBlockerLinks,
    pendingSuggestions,
    setPendingSuggestions,
    loading,
    setLoading,
    participantInfo,
    scenarioInfo,
    loadPathways,
    formCheck,
  };
}

export default useImpactPathData;
